/**
 * Who sees which commission layer. Agents see only the Agents layer; BD employees
 * see only BD Employees; people on an "other" bonus plan see Bonus; managers/admins
 * see the layers they oversee (and can toggle). The rosters live in /config/restricted,
 * which only admins can read, so for everyone else the flags come from the
 * `commissionMyAccess` callable (fetched once per sign-in, cached in this module).
 */
import { useEffect, useSyncExternalStore } from 'react';
import { resolveCommissionAgents, resolveCommissionTargets, resolveCommissionOtherPlans, type CommissionMyAccessResponse } from '@daaily/shared';
import { useProfile } from '../profile/ProfileProvider';
import { useIsCommissionsAdmin } from './useIsCommissionsAdmin';
import { callCommissionMyAccess } from './callables';

const NONE: CommissionMyAccessResponse = { isBd: false, isAgent: false, managesAgents: false, isOtherPerson: false, managesOther: false };
let cachedFor = '';
let cached: CommissionMyAccessResponse | null = null;
let inflight: Promise<void> | null = null;
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
function ensureLoaded(email: string) {
  if (cachedFor === email && (cached || inflight)) return;
  cachedFor = email; cached = null;
  inflight = callCommissionMyAccess({}).then((r) => { if (cachedFor === email) cached = r.data; })
    .catch(() => { if (cachedFor === email) cached = NONE; })
    .finally(() => { inflight = null; notify(); });
}
function useServerAccess(email: string, enabled: boolean): CommissionMyAccessResponse | null {
  useEffect(() => { if (enabled && email) ensureLoaded(email); }, [email, enabled]);
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => { subs.delete(cb); }; },
    () => (cachedFor === email ? cached : null),
  );
}

export function useCommissionAccess(): {
  isAdmin: boolean;
  isAgent: boolean;
  managesAgents: boolean;
  isBd: boolean;
  hasAgentLayer: boolean;
  hasBdLayer: boolean;
  /** On an "other" (person-specific bonus) plan — sees the layer as Bonus. */
  isOtherPerson: boolean;
  managesOther: boolean;
  hasOtherLayer: boolean;
  /** False while the server flags are still loading (non-admins only). */
  resolved: boolean;
} {
  const { state } = useProfile();
  const isAdmin = useIsCommissionsAdmin();
  const config = state.status === 'ready' ? state.config : null;
  const email = state.status === 'ready' ? state.user.email.toLowerCase() : '';
  const enabled = state.status === 'ready' && !!state.user.tools?.commissions?.enabled;
  // Admins read the rosters straight from the (merged) config; everyone else asks the server.
  const canReadRosters = isAdmin || !!(config && (config.commissionOtherPlans || config.commissionAgents || config.commissionTargets));
  const server = useServerAccess(email, enabled && !canReadRosters);

  const local = {
    isAgent: !!email && resolveCommissionAgents(config).some((a) => (a.userEmail ?? '').toLowerCase() === email),
    managesAgents: !!email && resolveCommissionAgents(config).some((a) => (a.managerEmail ?? '').toLowerCase() === email),
    isBd: !!email && resolveCommissionTargets(config).some((t) => (t.userEmail ?? '').toLowerCase() === email),
    isOtherPerson: !!email && resolveCommissionOtherPlans(config).some((p) => (p.userEmail ?? '').toLowerCase() === email && p.active !== false),
    managesOther: !!email && resolveCommissionOtherPlans(config).some((p) => (p.managerEmail ?? '').toLowerCase() === email),
  };
  const f = canReadRosters ? local : (server ?? NONE);
  const resolved = canReadRosters || !enabled || server !== null;

  const hasAgentLayer = isAdmin || f.isAgent || f.managesAgents;
  const hasOtherLayer = isAdmin || f.isOtherPerson || f.managesOther;
  // BD layer: admins, linked BDs, or (fallback) anyone provisioned who is NOT a
  // pure agent / other-plan person — the existing BD statement view already shows to provisioned users.
  const hasBdLayer = isAdmin || f.isBd || (!f.isAgent && !f.managesAgents && !f.isOtherPerson && !f.managesOther);

  return { isAdmin, isAgent: f.isAgent, managesAgents: f.managesAgents, isBd: f.isBd, hasAgentLayer, hasBdLayer, isOtherPerson: f.isOtherPerson, managesOther: f.managesOther, hasOtherLayer, resolved };
}
